// src/utils/lossRunStatus.js
//
// Pure status logic for the Loss Runs tab. No I/O. Given the shared source
// directory (FIG_LossRunSources), one client's policies (FIG_Policies) and one
// client's request log (FIG_LossRunRequests), it yields one line per source
// that has at least one of the client's policies assigned, plus one
// "Unassigned" line when any policy has a blank LossRunSourceId.
//
// Null rules (same shape as homesCalc): an absent boolean is UNKNOWN, never
// false; a blank or unparseable date is unknown, never zero and never today.
// SharePoint dateOnly values arrive as "2026-10-08T07:00:00Z" — the first ten
// characters are the date; nothing here converts to local time.

import { parseLocalDate } from './parseLocalDate';

export const LOSS_RUN_FRESH_DAYS = 120;
export const AUTHORIZATION_FRESH_DAYS = 365;
export const UNASSIGNED_ID = '';

const COLOR_ORDER = { red: 0, yellow: 1, grey: 2, green: 3 };

// "2026-10-08T07:00:00Z" | "2026-10-08" | "10/08/2026" | Date -> "2026-10-08" | null
export function dateOnlyOf(value) {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
  }
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = parseLocalDate(s);
  if (!d) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Whole days from a to b, both "YYYY-MM-DD", computed in UTC so no DST or
// local-offset drift. null when either side is unknown.
export function daysBetween(a, b) {
  const da = dateOnlyOf(a), db = dateOnlyOf(b);
  if (!da || !db) return null;
  const [ay, am, ad] = da.split('-').map(Number);
  const [by, bm, bd] = db.split('-').map(Number);
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86400000);
}

// Policy term label from the parsed EffectiveDate year: 2023 -> "2023-24".
export function termOf(policy) {
  const d = parseLocalDate(policy?.EffectiveDate);
  if (!d) return null;
  const y = d.getFullYear();
  return `${y}-${String(y + 1).slice(-2)}`;
}

const blank = v => v === null || v === undefined || String(v).trim() === '';
const idOf = v => (blank(v) ? UNASSIGNED_ID : String(v).trim());

// Submitted and Quoted rows are not policies to pull loss runs on. Trimmed,
// case-insensitive on Status. Everything else (Active, Bound, Expired,
// Cancelled, Terminated, blank) is eligible.
const EXCLUDED_STATUSES = new Set(['submitted', 'quoted']);
export function isLossRunEligible(policy) {
  return !EXCLUDED_STATUSES.has(String(policy?.Status ?? '').trim().toLowerCase());
}

// Absent boolean -> null (unknown). Only a real boolean (or its string form) counts.
export function boolOrNull(v) {
  if (v === true || v === false) return v;
  if (v === 'true') return true;
  if (v === 'false') return false;
  return null;
}

// The insured's loss-run authorization letter, read off the FIG_Insureds row.
//   onFile: true | false | null (unknown)
//   signed: "YYYY-MM-DD" | null
//   stale:  true when signed is more than AUTHORIZATION_FRESH_DAYS before today;
//           false when fresh; null when there is no usable signed date
//   usable: true only when on file and not stale (a fresh letter the Ask can attach)
export function authorizationState(insured, today) {
  const onFile = boolOrNull(insured?.LossRunAuthOnFile);
  const signed = dateOnlyOf(insured?.LossRunAuthSignedDate);
  const age = signed ? daysBetween(signed, dateOnlyOf(today) || dateOnlyOf(new Date())) : null;
  const stale = age === null ? null : age > AUTHORIZATION_FRESH_DAYS;
  return {
    onFile,
    signed,
    stale,
    vaultPath: insured?.LossRunAuthVaultPath ?? '',
    usable: onFile === true && stale !== true,
  };
}

// ── Authorization need, derived per policy ───────────────────────────────────
// A policy needs the insured's authorization when its AgencyOfRecord is present
// and is not our own agency. Blank AgencyOfRecord is unknown. The source's
// RequiresInsuredAuthorization is an override only: explicit true/false wins,
// absent means use the derived value. The Ask draft and the handoff PDF read
// the same function, so they can never disagree with the card.
export const OWN_AGENCY_NAME = 'Fragos Insurance Group';

// true | false | null (unknown)
export function policyNeedsAuthorization(policy) {
  const aor = policy?.AgencyOfRecord;
  if (blank(aor)) return null;
  return String(aor).trim().toLowerCase() !== OWN_AGENCY_NAME.toLowerCase();
}

/**
 * authorizationNeed(source, policies) over the eligible policies
 * → { need: 'needed'|'not needed'|'unknown', needing, unknown, total, override }
 */
export function authorizationNeed(source, policies) {
  const eligible = (policies || []).filter(isLossRunEligible);
  let needing = 0, unknown = 0;
  for (const p of eligible) {
    const v = policyNeedsAuthorization(p);
    if (v === true) needing++;
    else if (v === null) unknown++;
  }
  const derived = needing > 0 ? 'needed' : unknown > 0 ? 'unknown' : 'not needed';
  const override = boolOrNull(source?.RequiresInsuredAuthorization);
  const need = override === true ? 'needed' : override === false ? 'not needed' : derived;
  return { need, needing, unknown, total: eligible.length, override, derived };
}

// The authorization note for one source. null when nothing should be said.
// `color` is the card's color: a green card only needs the letter for its NEXT
// request; any other color needs it before the ask that is pending.
export function authorizationNote(needInfo, auth, color) {
  const info = typeof needInfo === 'string' ? { need: needInfo, needing: 0, total: 0, override: null } : (needInfo || {});
  if (info.need === 'unknown') return 'Authorization need unknown';
  if (info.need !== 'needed') return null;
  const lead = color === 'green' ? 'Letter needed for the next request' : 'Letter needed before you ask';
  if (!auth || auth.onFile !== true) {
    const why = info.needing > 0
      ? `${info.needing} of ${info.total} polic${info.total === 1 ? 'y was' : 'ies were'} written by another agency`
      : 'required by source';
    return `${lead} (${why})`;
  }
  if (auth.stale === true) return `${lead} (last one signed ${auth.signed})`;
  if (!auth.signed) return 'Authorization on file, date unknown';
  return `Authorization on file, signed ${auth.signed}`;
}

function effectiveTime(p) {
  const d = parseLocalDate(p?.EffectiveDate);
  return d ? d.getTime() : null;
}
function newestFirst(policies) {
  return [...policies].sort((a, b) => {
    const ta = effectiveTime(a), tb = effectiveTime(b);
    if (ta === null && tb === null) return 0;
    if (ta === null) return 1;
    if (tb === null) return -1;
    return tb - ta;
  });
}

// Requests newest first: by DateRequested (falling back to DateReceived)
// descending, unknown dates last, then higher SharePoint id.
export function sortRequestsNewestFirst(requests) {
  const key = r => dateOnlyOf(r.DateRequested) || dateOnlyOf(r.DateReceived);
  return [...(requests || [])].sort((a, b) => {
    const ka = key(a), kb = key(b);
    if (ka && kb && ka !== kb) return ka < kb ? 1 : -1;
    if (ka && !kb) return -1;
    if (!ka && kb) return 1;
    return (Number(b.id) || 0) - (Number(a.id) || 0);
  });
}

// Latest request, or null when there are none.
export function latestRequestOf(requests) {
  if (!requests || requests.length === 0) return null;
  return sortRequestsNewestFirst(requests)[0];
}

// ── Per-policy request matching ──────────────────────────────────────────────
// A FIG_LossRunRequests row covers a policy when its LossRunSourceId matches
// the policy's and its PolicyIds is blank (= every eligible policy of that
// source) or lists the policy's FIG_Policies item id.
export function parsePolicyIds(v) {
  return String(v ?? '').split(/[,;\s]+/).map(x => x.trim()).filter(Boolean);
}
export function requestCoversPolicy(request, policy) {
  if (!request || !policy) return false;
  if (idOf(request.LossRunSourceId) !== idOf(policy.LossRunSourceId)) return false;
  if (blank(request.PolicyIds)) return true;
  return parsePolicyIds(request.PolicyIds).includes(String(policy.id));
}
export function requestsForPolicy(requests, policy) {
  return (requests || []).filter(r => requestCoversPolicy(r, policy));
}

// Severity for "worst of": nothing requested is the most urgent, then blocked,
// then waiting/stale, then current.
const WORST_ORDER = { red: 0, grey: 1, yellow: 2, green: 3 };
export function worstColor(colors) {
  let best = null;
  for (const c of colors || []) if (c in WORST_ORDER && (best === null || WORST_ORDER[c] < WORST_ORDER[best])) best = c;
  return best;
}

// Source-level blocks that make a request impossible (apply to every policy).
export function sourceGreyReasons(source, { unassigned = false } = {}) {
  const grey = [];
  if (unassigned) grey.push('No source assigned');
  else if (!source) grey.push('Source not found');
  if (source && boolOrNull(source.DoNotContact) === true) grey.push('Do not contact');
  if (source && String(source.DefaultMethod ?? '') === 'Skip') grey.push('Skipped by choice');
  if (!unassigned && source && blank(source.ContactEmail) && blank(source.ContactPhone)) grey.push('No contact on file');
  return grey;
}

/**
 * policyState({ policy, source, requests, today })
 * → { color, reasons, latestRequest } for ONE policy, by the same rules as the
 *   cards. Green/yellow come from the latest request that covers THIS policy
 *   and stand regardless of a missing contact: a run on file is on file.
 *   Grey applies only to a policy that still needs something (would be red):
 *   a blocked source, no source, a blank policy number. Do-not-contact is the
 *   one exception — it is grey always.
 */
export function policyState({ policy, source, requests, today, unassigned = false }) {
  const todayKey = dateOnlyOf(today) || dateOnlyOf(new Date());
  const latestRequest = unassigned ? null : latestRequestOf(requestsForPolicy(requests, policy));
  if (source && boolOrNull(source.DoNotContact) === true) {
    return { color: 'grey', reasons: ['Do not contact'], latestRequest };
  }
  const st = requestState(latestRequest, todayKey);
  if (st.color !== 'red') return { color: st.color, reasons: st.reasons, latestRequest };
  const grey = sourceGreyReasons(source, { unassigned });
  if (blank(policy?.PolicyNumber)) grey.push('Policy number missing');
  if (grey.length) return { color: 'grey', reasons: grey, latestRequest };
  return { color: 'red', reasons: st.reasons, latestRequest };
}

export function requestState(latest, today) {
  if (!latest) return { color: 'red', reasons: ['Not requested'] };
  const method = blank(latest.Method) ? 'Loss run' : String(latest.Method);
  const received = dateOnlyOf(latest.DateReceived);
  if (received) {
    const asOf = dateOnlyOf(latest.ValuedAsOf) || received;
    const age = daysBetween(asOf, today);
    if (age !== null && age <= LOSS_RUN_FRESH_DAYS) {
      return { color: 'green', reasons: [`${method} on file, valued as of ${asOf}`] };
    }
    return { color: 'yellow', reasons: [`On file, dated ${asOf}, refresh before renewal`] };
  }
  const requested = dateOnlyOf(latest.DateRequested);
  if (requested) return { color: 'yellow', reasons: [`Requested ${requested}, waiting`] };
  return { color: 'yellow', reasons: ['Request logged, dates unknown, waiting'] };
}

function buildLine({ sourceId, source, policies, requests, today, auth }) {
  const unassigned = sourceId === UNASSIGNED_ID;
  const sorted = newestFirst(policies);
  const terms = [...new Set(sorted.map(p => termOf(p) || 'term unknown'))].sort();

  const needInfo = source ? authorizationNeed(source, sorted) : null;
  const doNotContact = source ? boolOrNull(source.DoNotContact) : null;
  const contactEmail = source?.ContactEmail ?? '';
  const contactPhone = source?.ContactPhone ?? '';
  const defaultMethod = source?.DefaultMethod ?? '';

  // Per-policy states (a request covers only the policies its PolicyIds name,
  // or all of the source's when blank). The card is the worst of them.
  const policyStates = new Map(sorted.map(p => [String(p.id), policyState({ policy: p, source, requests, today, unassigned })]));
  const states = [...policyStates.values()];
  const color = worstColor(states.map(st => st.color)) || 'red';
  const total = states.length;
  // Aggregate reasons: identical everywhere -> as is; otherwise one line per
  // distinct state, worst first, prefixed with its share of the policies.
  const groups = new Map();
  for (const st of states) {
    const key = `${st.color}|${st.reasons.join(' · ')}`;
    if (!groups.has(key)) groups.set(key, { color: st.color, reasons: st.reasons, n: 0 });
    groups.get(key).n++;
  }
  const grouped = [...groups.values()].sort((a, b) => WORST_ORDER[a.color] - WORST_ORDER[b.color] || b.n - a.n);
  const stateReasons = grouped.length <= 1
    ? (grouped[0]?.reasons || ['Not requested'])
    : grouped.map(g => `${g.n} of ${total} polic${total === 1 ? 'y' : 'ies'}: ${g.reasons.join(', ')}`);
  const state = { color, reasons: stateReasons };
  const latestRequest = unassigned ? null : latestRequestOf(requests);

  // Authorization is a note, never a color: a missing letter makes the request
  // incomplete, not impossible.
  const notes = [];
  if (!unassigned && source) {
    const n = authorizationNote(needInfo, auth, color);
    if (n) notes.push(n);
  }
  // Grey reasons (source-level blocks) as their own list, so a short summary
  // can still say WHY nothing can be asked for.
  const greyReasons = [...new Set(states.filter(st => st.color === 'grey').flatMap(st => st.reasons))];

  return {
    sourceId,
    name: unassigned ? 'Unassigned' : (source?.Title || `Source #${sourceId}`),
    kind: source?.Kind ?? '',
    contactName: source?.ContactName ?? '',
    contactEmail,
    contactPhone,
    defaultMethod,
    notes: source?.Notes ?? '',
    requiresInsuredAuthorization: needInfo ? needInfo.override : null,
    authorizationNeed: needInfo ? needInfo.need : null,
    authorizationCounts: needInfo ? { needing: needInfo.needing, unknown: needInfo.unknown, total: needInfo.total } : null,
    doNotContact,
    policyCount: sorted.length,
    terms,
    lines: [...new Set(sorted.map(p => String(p.LOB ?? '').trim()).filter(Boolean))].sort(),
    policies: sorted,
    policyStates,
    latestRequest,
    color: state.color,
    reasons: [...state.reasons, ...notes],
    authNote: notes[0] || null,
    greyReasons,
  };
}

/**
 * lossRunLines({ sources, policies, requests, today })
 *   sources  — every FIG_LossRunSources row (shared directory)
 *   policies — this client's FIG_Policies rows (already filtered by InsuredId);
 *              Submitted and Quoted rows are dropped here (isLossRunEligible)
 *   requests — this client's FIG_LossRunRequests rows (already filtered)
 *   insured  — the FIG_Insureds row (LossRunAuth* columns); optional
 *   today    — "YYYY-MM-DD" or Date
 * Returns lines ordered red, yellow, grey, green, then by name.
 */
export function lossRunLines({ sources = [], policies = [], requests = [], insured = null, today } = {}) {
  const todayKey = dateOnlyOf(today) || dateOnlyOf(new Date());
  const byId = new Map((sources || []).map(s => [String(s.id), s]));
  const auth = authorizationState(insured, todayKey);

  const groups = new Map();
  for (const p of (policies || []).filter(isLossRunEligible)) {
    const sid = idOf(p.LossRunSourceId);
    if (!groups.has(sid)) groups.set(sid, []);
    groups.get(sid).push(p);
  }

  const lines = [];
  for (const [sourceId, group] of groups) {
    lines.push(buildLine({
      sourceId,
      source: sourceId === UNASSIGNED_ID ? null : byId.get(sourceId) || null,
      policies: group,
      requests: (requests || []).filter(r => idOf(r.LossRunSourceId) === sourceId),
      today: todayKey,
      auth,
    }));
  }

  return lines.sort((a, b) => {
    const c = COLOR_ORDER[a.color] - COLOR_ORDER[b.color];
    if (c !== 0) return c;
    if (a.sourceId === UNASSIGNED_ID) return 1;
    if (b.sourceId === UNASSIGNED_ID) return -1;
    return a.name.localeCompare(b.name);
  });
}

// Sources with no line for this client (so do-not-contact entries stay visible).
export function lossRunDirectory(sources, lines) {
  const used = new Set((lines || []).map(l => l.sourceId));
  return [...(sources || [])]
    .filter(s => !used.has(String(s.id)))
    .sort((a, b) => String(a.Title || '').localeCompare(String(b.Title || '')));
}

export function lossRunCounts(lines) {
  const counts = { green: 0, yellow: 0, red: 0, grey: 0 };
  for (const l of lines || []) if (l.color in counts) counts[l.color]++;
  return counts;
}
